import { useEffect, useRef, useState } from 'react'
import { Icon } from '../components/icons/Icon'
import { useDashboardTheme } from './DashboardThemeProvider'
import { DASHBOARD_THEME_PRESETS } from './dashboardThemePresets'

/**
 * Dashboard colour theme picker (IB-292) — mirrors `AccentPicker`'s compact
 * trigger + popover `radiogroup` mechanism exactly (design doc §4), with a
 * distinct icon (`palette`, not the Accent picker's colour-swatch trigger)
 * and a distinct `aria-label`/`title` ("Dashboard colour theme", not
 * "Appearance — accent colour") so a screen-reader/sighted user never
 * confuses the two controls when both are present (Accent picker in the top
 * bar, this control in a dashboard route's own header).
 *
 * Applies immediately on selection — no separate Apply/Save step (CLAUDE.md
 * §4 "every CTA opens a real page or a defined action").
 */
export function DashboardThemePicker() {
  const { dashboardTheme, setDashboardTheme } = useDashboardTheme()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return undefined
    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  return (
    <div className="iw-dashboard-theme-picker" ref={rootRef}>
      <button
        type="button"
        className="iw-dashboard-theme-picker__trigger"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Dashboard colour theme"
        title="Dashboard colour theme"
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="palette" size={16} />
      </button>
      {open ? (
        <div className="iw-dashboard-theme-picker__popover" role="dialog" aria-label="Dashboard colour theme">
          <div className="iw-dashboard-theme-picker__grid" role="radiogroup" aria-label="Dashboard colour theme">
            <button
              type="button"
              role="radio"
              aria-checked={dashboardTheme === null}
              title="Platform Default"
              className={
                'iw-dashboard-theme-picker__option' +
                (dashboardTheme === null ? ' iw-dashboard-theme-picker__option--active' : '')
              }
              onClick={() => setDashboardTheme(null)}
            >
              <span
                className="iw-dashboard-theme-picker__swatch iw-dashboard-theme-picker__swatch--default"
                aria-hidden="true"
              />
              Platform Default
            </button>
            {DASHBOARD_THEME_PRESETS.map((preset) => {
              const active = dashboardTheme === preset.id
              return (
                <button
                  key={preset.id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  title={preset.name}
                  className={
                    'iw-dashboard-theme-picker__option' + (active ? ' iw-dashboard-theme-picker__option--active' : '')
                  }
                  onClick={() => setDashboardTheme(preset.id)}
                >
                  {/* `preset.hex` is data read from the one sanctioned registry module
                      (dashboardThemePresets.ts) — same pattern AccentPicker's swatch
                      fills already use for ACCENT_PRESETS. */}
                  <span className="iw-dashboard-theme-picker__swatch" style={{ background: preset.hex }} aria-hidden="true" />
                  {preset.name}
                </button>
              )
            })}
          </div>
        </div>
      ) : null}
    </div>
  )
}
